/**
 * O que faz: busca no banco o resumo e a lista do painel "Leads para retomar hoje" e confere o formato da resposta.
 * Onde usa: useResumoRetomada e useListaRetomada (src/hooks/dashboard/useRetomadaDashboard.ts).
 * Conexões: RPCs get_dashboard_retomada_resumo_v1 e get_dashboard_retomada_lista_v1
 *   (migration 20261023150000_painel_retomada.sql) e regras de features/retomada.
 * Tabelas: somente leitura, pelas RPCs, de chat_mensagens, leads, vendas_realizadas e lead_returns da clínica.
 * Data: 22/10/2026.
 */
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { GRUPOS_RETOMADA, SITUACOES_RETOMADA, type GrupoRetomada, type SituacaoRetomada } from '@/features/retomada/regras';

const contagem = z.number().int().nonnegative();
const grupoSchema = z.enum(GRUPOS_RETOMADA);

// Tipos explícitos: o projeto usa "strict: false", e nesse modo o z.infer marca todos os campos como
// opcionais (e sem null), mesmo que o schema os exija. Declarar os tipos aqui mantém os campos
// obrigatórios e o "| null" exatamente onde o SQL pode devolver nulo, sem casts nos componentes.
export interface GrupoResumoRetomada {
  grupo: GrupoRetomada;
  total: number;
  pendentes: number;
  retomados: number;
}

export interface ResumoRetomada {
  hoje: string;
  ontem: string;
  grupos: GrupoResumoRetomada[];
  ontemResultado: { total: number; retomados: number; voltaram: number };
  atualizadoEm: string;
}

export interface ItemRetomada {
  id: string;
  clinica_id: string;
  nome: string | null;
  telefone: string | null;
  created_at: string | null;
  ad_name: string | null;
  origem_lead: string | null;
  grupo: GrupoRetomada;
  dias_parado: number;
  ultima_mensagem_em: string;
  ultimo_autor: 'lead' | 'clinica';
  trecho: string;
  ja_respondeu: boolean;
  situacao: SituacaoRetomada;
  retomado_em: string | null;
}

export interface ListaRetomada {
  dia: string;
  total: number;
  itens: ItemRetomada[];
}

// Os schemas continuam sendo a validação em tempo de execução. Com "strict: false" o TypeScript não
// aceita anotar o schema direto como z.ZodType<Tipo> (ele enxerga os campos como opcionais), por isso
// cada schema é convertido para o tipo explícito no fim. Para a conversão não esconder erros, o tipo
// "ConferirTipo" faz o compilador acusar qualquer campo cujo tipo diverga entre o schema e a interface.
type ConferirTipo<Esperado extends Real, Real> = Esperado;

const resumoBruto = z.object({
  hoje: z.string(),
  ontem: z.string(),
  // O banco sempre devolve os quatro grupos; menos que isso indica implantação incompleta.
  grupos: z.array(z.object({ grupo: grupoSchema, total: contagem, pendentes: contagem, retomados: contagem })).length(4),
  ontemResultado: z.object({ total: contagem, retomados: contagem, voltaram: contagem }),
  atualizadoEm: z.string(),
});

const itemBruto = z.object({
  id: z.string().uuid(),
  clinica_id: z.string().uuid(),
  nome: z.string().nullable(),
  telefone: z.string().nullable(),
  // leads.created_at é opcional (nullable) em produção: um lead antigo sem data não pode rejeitar a lista inteira.
  created_at: z.string().nullable(),
  ad_name: z.string().nullable(),
  origem_lead: z.string().nullable(),
  grupo: grupoSchema,
  dias_parado: z.number().int().min(1).max(7),
  ultima_mensagem_em: z.string(),
  ultimo_autor: z.enum(['lead', 'clinica']),
  trecho: z.string(),
  ja_respondeu: z.boolean(),
  situacao: z.enum(SITUACOES_RETOMADA),
  retomado_em: z.string().nullable(),
});

const listaBruta = z.object({
  dia: z.string(),
  total: contagem,
  itens: z.array(itemBruto).max(500),
});

// Se um campo mudar de tipo no schema e não na interface (ou ao contrário), estas linhas deixam de compilar.
export type ConfereResumoRetomada = ConferirTipo<ResumoRetomada, z.infer<typeof resumoBruto>>;
export type ConfereItemRetomada = ConferirTipo<ItemRetomada, z.infer<typeof itemBruto>>;
export type ConfereListaRetomada = ConferirTipo<ListaRetomada, z.infer<typeof listaBruta>>;

const resumoRetomadaSchema = resumoBruto as z.ZodType<ResumoRetomada>;
const listaRetomadaSchema = listaBruta as z.ZodType<ListaRetomada>;

type NomeRpcRetomada = 'get_dashboard_retomada_resumo_v1' | 'get_dashboard_retomada_lista_v1';

async function consultarRpcRetomada<TSchema extends z.ZodTypeAny>(
  nome: NomeRpcRetomada,
  clinicaId: string,
  schema: TSchema,
  signal?: AbortSignal,
): Promise<z.output<TSchema>> {
  // Sem clínica válida não há consulta: o banco recusaria de qualquer forma e o erro ficaria confuso.
  if (!z.string().uuid().safeParse(clinicaId).success) throw new Error('Clínica inválida para o painel de retomada.');
  signal?.throwIfAborted();
  // RPC somente leitura (SECURITY DEFINER com guarda de clínica/admin no banco). Único filtro: p_clinica_id.
  // p_hoje fica de fora para o banco usar o dia atual de São Paulo. Risco: a lista traz nome e telefone,
  // por isso a resposta nunca vai para log ou console.
  let consulta = supabase.rpc(nome, { p_clinica_id: clinicaId });
  if (signal) consulta = consulta.abortSignal(signal);
  const { data, error } = await consulta;
  signal?.throwIfAborted();
  if (error) {
    throw new Error(error.code === '42501'
      ? 'Você não tem acesso aos dados desta clínica.'
      : 'Não foi possível carregar os leads para retomar. Tente novamente.');
  }
  const resultado = schema.safeParse(data);
  if (!resultado.success) throw new Error('Os dados do painel de retomada vieram incompletos. Atualize e tente novamente.');
  return resultado.data;
}

export const buscarResumoRetomada = (clinicaId: string, signal?: AbortSignal): Promise<ResumoRetomada> =>
  consultarRpcRetomada('get_dashboard_retomada_resumo_v1', clinicaId, resumoRetomadaSchema, signal);

export const buscarListaRetomada = (clinicaId: string, signal?: AbortSignal): Promise<ListaRetomada> =>
  consultarRpcRetomada('get_dashboard_retomada_lista_v1', clinicaId, listaRetomadaSchema, signal);
